import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/pause/motion";
import { scrollToId } from "@/lib/smooth";

function fmtRange(start, end) {
  const opts = { day: "numeric", month: "short", year: "numeric" };
  try {
    const s = new Date(start).toLocaleDateString(undefined, opts);
    if (!end || end === start) return s;
    const e = new Date(end).toLocaleDateString(undefined, opts);
    return `${s} — ${e}`;
  } catch {
    return start;
  }
}

const EventCard = ({ ev, index }) => {
  const reverse = index % 2 === 1;
  return (
    <Reveal delay={0.05}>
      <div
        data-testid={`event-card-${ev.id}`}
        className={`grid grid-cols-1 min-[900px]:grid-cols-12 gap-8 min-[900px]:gap-12 items-center ${
          reverse ? "min-[900px]:[&>div:first-child]:order-2" : ""
        }`}
      >
        {/* Cover */}
        <div className="min-[900px]:col-span-6">
          <div className="relative overflow-hidden rounded-sm aspect-[4/3] bg-[#F2EDE5] shadow-[0_20px_50px_rgba(26,26,26,0.10)]">
            {ev.cover_image ? (
              <motion.img
                initial={{ scale: 1.08 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                src={ev.cover_image}
                alt={ev.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#8C8070] font-display text-3xl">
                Pause<span className="text-[#F5C800]">.</span>
              </div>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="min-[900px]:col-span-6">
          <p className="font-body text-xs uppercase tracking-[0.22em] text-[#3B5A3A] mb-4">
            {fmtRange(ev.date_start, ev.date_end)}
            {ev.duration ? ` · ${ev.duration}` : ""}
          </p>
          <h3 className="font-display font-light text-4xl md:text-5xl leading-[1.02] tracking-tight text-[#1A1A1A]">
            {ev.title}
          </h3>
          <p className="mt-4 font-body text-sm inline-flex items-center gap-2 text-[#8C8070]">
            <MapPin className="h-3.5 w-3.5" /> {ev.location}
          </p>
          {ev.description && (
            <p className="mt-5 font-body text-base md:text-lg text-[#1A1A1A]/70 leading-relaxed max-w-xl">
              {ev.description}
            </p>
          )}
          {ev.activities?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {ev.activities.map((a) => (
                <span
                  key={a}
                  className="font-body text-[13px] px-3.5 py-1.5 rounded-full border border-[#8C8070]/40 text-[#1A1A1A]/80"
                >
                  {a}
                </span>
              ))}
            </div>
          )}
          <button
            onClick={() => scrollToId("#waitlist")}
            data-testid={`event-reserve-${ev.id}`}
            className="mt-8 font-body text-sm uppercase tracking-[0.12em] font-medium bg-[#1A1A1A] text-[#FAF7F2] px-6 py-3 rounded-full inline-flex items-center gap-2 hover:scale-[1.03] transition-transform"
          >
            Reserve a spot
            <ArrowUpRight className="h-4 w-4" />
          </button>

          {/* Gallery thumbs */}
          {ev.gallery_images?.length > 0 && (
            <div className="mt-8 grid grid-cols-4 gap-2 max-w-md">
              {ev.gallery_images.slice(0, 4).map((src, i) => (
                <div key={i} className="aspect-square overflow-hidden rounded-sm">
                  <img src={src} alt={`${ev.title} ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Reveal>
  );
};

export const Events = () => {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/events")
      .then((res) => setEvents(res.data || []))
      .catch(() => setEvents([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || events.length === 0) return null;

  return (
    <section id="events" data-testid="events-section" className="bg-[#F2EDE5] py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <div className="flex items-baseline justify-between gap-4 flex-wrap mb-4">
            <p className="font-body text-xs uppercase tracking-[0.28em] text-[#3B5A3A] flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5" /> Upcoming
            </p>
            <p className="font-body text-xs uppercase tracking-[0.16em] text-[#8C8070]">
              {events.length} {events.length === 1 ? "event" : "events"} on the calendar
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display font-light text-5xl md:text-7xl leading-[0.95] tracking-tight text-[#1A1A1A] mb-14 max-w-3xl">
            Retreats &amp; <span className="italic text-[#3B5A3A]">experiences</span> ahead.
          </h2>
        </Reveal>

        <div className="space-y-20 md:space-y-28">
          {events.map((ev, i) => (
            <EventCard key={ev.id} ev={ev} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
